import React, { forwardRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSizes, SearchIcon } from 'libs/shared/ui';

import { styled } from '@mui/material';

import { Colors } from 'Themes/colors';

const SearchInputWrapper = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchIconWrapper = styled('div')`
  display: flex;
  align-items: center;
  color: ${Colors.darkGrey};
  margin-right: 8px;
`;

const StyledInput = styled('input')`
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 120px;
  font-size: 14px;
  padding: 4px 0;

  &::placeholder {
    color: ${Colors.darkGrey};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type SearchInputProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
}>;

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { value, onChange, onKeyDown, onFocus, placeholder, disabled = false, hasError = false },
    ref
  ) => {
    const { t } = useTranslation();

    return (
      <SearchInputWrapper>
        <SearchIconWrapper>
          <SearchIcon fontSize={IconSizes.small} />
        </SearchIconWrapper>
        <StyledInput
          ref={ref}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={t('coursesPage.searchPlaceholder', 'Search courses and filter by tags')}
          aria-invalid={hasError}
          role="textbox"
        />
      </SearchInputWrapper>
    );
  }
);
